/**
 * ProviderTimeline — email thread view for a single provider.
 * Adapted from frontend/src/components/ProviderTimeline.tsx for React Native.
 */
import { useState, useEffect, useRef } from 'react'
import {
  View, Text, Pressable, ScrollView, TextInput, StyleSheet,
  ActivityIndicator, Platform, KeyboardAvoidingView,
} from 'react-native'
import {
  ArrowUpRight, ArrowDownLeft, ArrowLeft, Paperclip, FileText,
  Send, Sparkles, Mail,
} from 'lucide-react-native'
import { apiClient } from '../api/client'
import { appEvents } from '../lib/events'
import EmailBody from './EmailBody'
import { colors, spacing, borderRadius, typography, shadows } from '../theme'
import { toast } from './Toast'

export interface TimelineMessage {
  id: string
  subject: string
  body: string
  body_html?: string
  direction: 'inbound' | 'outbound'
  message_type: string
  status: string
  from_email?: string
  to_email?: string
  attachments?: { id?: string; filename: string; content_type: string; size: number }[]
  parsed_data?: any
  received_at?: string
  sent_at?: string
  created_at?: string
}

interface ProviderTimelineProps {
  providerId: string
  providerName: string
  onClose?: () => void
  readOnly?: boolean
  preloadedMessages?: TimelineMessage[]
}

export default function ProviderTimeline({
  providerId,
  providerName,
  onClose,
  readOnly = false,
  preloadedMessages,
}: ProviderTimelineProps) {
  const [messages, setMessages] = useState<TimelineMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [showCompose, setShowCompose] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [composeSubject, setComposeSubject] = useState('')
  const [composeBody, setComposeBody] = useState('')
  const [composeTo, setComposeTo] = useState('')
  const [sending, setSending] = useState(false)
  const scrollRef = useRef<ScrollView>(null)

  useEffect(() => {
    if (preloadedMessages) {
      setMessages(preloadedMessages)
      setLoading(false)
      const lastInbound = preloadedMessages.filter(m => m.direction === 'inbound').pop()
      if (lastInbound?.from_email) setComposeTo(lastInbound.from_email)
    } else {
      fetchTimeline()
    }
  }, [providerId, preloadedMessages])

  useEffect(() => {
    // Scroll to bottom when messages change
    if (scrollRef.current) {
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: false }), 100)
    }
  }, [messages])

  const fetchTimeline = async () => {
    setLoading(true)
    try {
      const res = await apiClient.inbox.providerTimeline(providerId)
      setMessages(res.data?.timeline || [])
      const lastInbound = (res.data?.timeline || [])
        .filter((m: TimelineMessage) => m.direction === 'inbound')
        .pop()
      if (lastInbound?.from_email) {
        setComposeTo(lastInbound.from_email)
      }
    } catch {
      toast.error('Kunne ikke hente samtale')
    } finally {
      setLoading(false)
    }
  }

  const generateDraft = async () => {
    setGenerating(true)
    try {
      const res = await apiClient.inbox.composeDraft(providerId)
      const data = res.data
      setComposeSubject(data.subject || '')
      setComposeBody(data.body || '')
      if (data.to_email) setComposeTo(data.to_email)
      setShowCompose(true)
    } catch {
      toast.error('Kunne ikke generere forslag')
    } finally {
      setGenerating(false)
    }
  }

  const handleSend = async () => {
    if (!composeTo || !composeSubject.trim() || !composeBody.trim()) {
      toast.error('Udfyld alle felter')
      return
    }
    setSending(true)
    try {
      const lastInbound = messages
        .filter((m) => m.direction === 'inbound')
        .pop()
      await apiClient.inbox.sendComposed({
        to_email: composeTo,
        subject: composeSubject,
        body: composeBody,
        in_reply_to_message_id: lastInbound?.id,
      })
      toast.success('Email sendt!')
      setShowCompose(false)
      setComposeSubject('')
      setComposeBody('')
      fetchTimeline()
      appEvents.emitDebounced('inbox-updated')
    } catch (error: any) {
      const msg = error?.response?.data?.detail || 'Kunne ikke sende email'
      toast.error(msg)
    } finally {
      setSending(false)
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    return d.toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    )
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
    >
      {/* Header */}
      {onClose && (
        <View style={styles.header}>
          <Pressable onPress={onClose} style={styles.backBtn} hitSlop={12}>
            <ArrowLeft size={20} color={colors.text.secondary} />
          </Pressable>
          <View style={styles.headerIcon}>
            <Mail size={18} color={colors.primary} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.headerTitle} numberOfLines={1}>{providerName}</Text>
            <Text style={styles.headerSub}>
              {messages.length} besked{messages.length !== 1 ? 'er' : ''}
            </Text>
          </View>
        </View>
      )}

      {/* Messages */}
      <ScrollView
        ref={scrollRef}
        style={styles.messageList}
        contentContainerStyle={messages.length === 0 ? styles.emptyContent : undefined}
      >
        {messages.length === 0 && (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              Ingen beskeder endnu med {providerName}.
            </Text>
          </View>
        )}

        {messages.map((msg) => {
          const isOutbound = msg.direction === 'outbound'
          const isAutoReply = msg.parsed_data?.auto_generated
          return (
            <View key={msg.id} style={styles.messageCard}>
              {/* Header row */}
              <View style={styles.messageHeader}>
                <View style={styles.messageHeaderLeft}>
                  {isOutbound ? (
                    <ArrowUpRight size={14} color={colors.text.muted} />
                  ) : (
                    <ArrowDownLeft size={14} color={colors.primary} />
                  )}
                  <Text style={styles.messageFrom} numberOfLines={1}>
                    {isOutbound ? (msg.to_email || 'Dig') : (msg.from_email || providerName)}
                  </Text>
                  {isAutoReply && (
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>Auto</Text>
                    </View>
                  )}
                  {msg.parsed_data?.classification && msg.direction === 'inbound' && (
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>
                        {msg.parsed_data.classification === 'quote' ? 'Tilbud' :
                         msg.parsed_data.classification === 'bill' ? 'Regning' :
                         msg.parsed_data.classification === 'notice' ? 'Varsel' :
                         msg.parsed_data.classification}
                      </Text>
                    </View>
                  )}
                </View>
                <Text style={styles.messageDate}>
                  {formatDate(msg.received_at || msg.sent_at)}
                </Text>
              </View>

              {/* Subject */}
              <Text style={styles.messageSubject}>{msg.subject}</Text>

              {/* Body — HTML for inbound, plain text for outbound (matches Next.js) */}
              {msg.body_html && msg.direction === 'inbound' ? (
                <EmailBody html={msg.body_html} plainText={msg.body || ''} />
              ) : (
                <Text style={styles.messageBody}>{msg.body}</Text>
              )}

              {/* Attachments */}
              {msg.attachments && msg.attachments.length > 0 && (
                <View style={styles.attachments}>
                  {msg.attachments.map((att, i) => (
                    <Pressable
                      key={i}
                      style={styles.attachmentPill}
                      onPress={async () => {
                        try {
                          const res = await apiClient.inbox.downloadAttachment(String(msg.id), att.id || String(i))
                          if (Platform.OS === 'web') {
                            const url = URL.createObjectURL(res.data)
                            const a = document.createElement('a')
                            a.href = url
                            a.download = att.filename || 'attachment'
                            a.click()
                            URL.revokeObjectURL(url)
                          } else {
                            toast('Vedhæftning downloadet')
                          }
                        } catch {
                          toast('Kunne ikke downloade vedhæftning')
                        }
                      }}
                    >
                      <Paperclip size={12} color={colors.text.muted} />
                      <Text style={styles.attachmentText} numberOfLines={1}>{att.filename}</Text>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          )
        })}
      </ScrollView>

      {/* Compose area */}
      {!readOnly && !showCompose && (
        <View style={styles.composeBar}>
          <Pressable
            onPress={generateDraft}
            disabled={generating}
            style={[styles.aiBtn, generating && { opacity: 0.5 }]}
          >
            {generating ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Sparkles size={16} color="#fff" />
            )}
            <Text style={styles.aiBtnText}>
              {generating ? 'Genererer...' : 'AI Forslag'}
            </Text>
          </Pressable>
          <Pressable
            onPress={() => {
              setShowCompose(true)
              const lastMsg = messages[messages.length - 1]
              if (lastMsg) {
                const subj = lastMsg.subject.startsWith('Re:') ? lastMsg.subject : `Re: ${lastMsg.subject}`
                setComposeSubject(subj)
              }
            }}
            style={styles.manualBtn}
          >
            <Send size={16} color={colors.text.secondary} />
            <Text style={styles.manualBtnText}>Skriv selv</Text>
          </Pressable>
        </View>
      )}

      {!readOnly && showCompose && (
        <View style={styles.composeForm}>
          <View style={styles.composeFields}>
            <View style={{ flex: 1 }}>
              <Text style={styles.composeLabel}>Til</Text>
              <TextInput
                style={styles.composeInput}
                value={composeTo}
                onChangeText={setComposeTo}
                placeholder="email@selskab.dk"
                placeholderTextColor={colors.text.muted}
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.composeLabel}>Emne</Text>
              <TextInput
                style={styles.composeInput}
                value={composeSubject}
                onChangeText={setComposeSubject}
                placeholder="Emne"
                placeholderTextColor={colors.text.muted}
              />
            </View>
          </View>
          <TextInput
            style={[styles.composeInput, { height: 100, textAlignVertical: 'top' }]}
            value={composeBody}
            onChangeText={setComposeBody}
            multiline
            placeholder="Skriv din besked..."
            placeholderTextColor={colors.text.muted}
          />
          <View style={styles.composeActions}>
            <Pressable
              onPress={() => { setShowCompose(false); setComposeBody(''); setComposeSubject('') }}
            >
              <Text style={styles.cancelText}>Annuller</Text>
            </Pressable>
            <Pressable
              onPress={handleSend}
              disabled={sending || !composeBody.trim()}
              style={[styles.sendBtn, (sending || !composeBody.trim()) && { opacity: 0.5 }]}
            >
              {sending ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Send size={16} color="#fff" />
              )}
              <Text style={styles.sendBtnText}>
                {sending ? 'Sender...' : 'Send'}
              </Text>
            </Pressable>
          </View>
        </View>
      )}
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
  },
  backBtn: {
    padding: 8,
  },
  headerIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: colors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    ...typography.captionBold,
    color: colors.text.primary,
  },
  headerSub: {
    ...typography.small,
    color: colors.text.muted,
  },

  // Messages
  messageList: {
    flex: 1,
    backgroundColor: colors.background,
  },
  emptyContent: {
    flex: 1,
    justifyContent: 'center',
  },
  emptyContainer: {
    alignItems: 'center',
    padding: spacing.xl,
  },
  emptyText: {
    ...typography.caption,
    color: colors.text.muted,
    textAlign: 'center',
  },
  messageCard: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  messageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  messageHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  messageFrom: {
    ...typography.small,
    fontWeight: '500',
    color: colors.text.primary,
    flex: 1,
  },
  badge: {
    backgroundColor: colors.surface,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  badgeText: {
    fontSize: 10,
    color: colors.text.muted,
  },
  messageDate: {
    fontSize: 11,
    color: colors.text.muted,
    marginLeft: 8,
    flexShrink: 0,
  },
  messageSubject: {
    ...typography.captionBold,
    color: colors.text.primary,
    marginBottom: 4,
  },
  messageBody: {
    ...typography.caption,
    color: colors.text.secondary,
  },
  attachments: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
  },
  attachmentPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.surface,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  attachmentText: {
    ...typography.small,
    color: colors.text.muted,
  },

  // Compose bar
  composeBar: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.background,
  },
  aiBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.md,
  },
  aiBtnText: {
    ...typography.captionBold,
    color: '#fff',
  },
  manualBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: borderRadius.md,
  },
  manualBtnText: {
    ...typography.captionBold,
    color: colors.text.secondary,
  },

  // Compose form
  composeForm: {
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.background,
    padding: spacing.md,
    gap: spacing.sm,
  },
  composeFields: {
    flexDirection: 'row',
    gap: 8,
  },
  composeLabel: {
    ...typography.small,
    fontWeight: '500',
    color: colors.text.muted,
    marginBottom: 2,
  },
  composeInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: Platform.OS === 'ios' ? 10 : 8,
    ...typography.caption,
    color: colors.text.primary,
  },
  composeActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cancelText: {
    ...typography.caption,
    color: colors.text.muted,
  },
  sendBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.md,
  },
  sendBtnText: {
    ...typography.captionBold,
    color: '#fff',
  },
})
